import { Button, Table, addToast, showDialogConfirm } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loading from '../../../../../../../components/info/Loading';
import TableEmptyContent from '../../../../../../../components/text/TableEmptyContent';

import { CostCenterItem, deleteCostCenter, getCostCenters } from './api';
import { CostCenterModal } from './components/CostCenterModal';
import { useCostCenterColumns } from './hooks/useCostCenterColumns';

const dictPrefix = 'financialPortal.settings.costCenters';

export default function CostCentersSection() {
  const { t } = useTranslation();
  const [items, setItems] = useState<CostCenterItem[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<CostCenterItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getCostCenters();
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.loadError`),
        });
      } else {
        setItems(data);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const handleNew = useCallback(() => {
    setEditing(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((item: CostCenterItem) => {
    setEditing(item);
    setIsModalOpen(true);
  }, []);

  const handleSaved = useCallback(() => {
    setIsModalOpen(false);
    setEditing(null);
    setRefreshToken((prev) => prev + 1);
  }, []);

  const handleDelete = useCallback(
    (item: CostCenterItem) => {
      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t(`${dictPrefix}.confirmDelete.title`),
        subtitle: t(`${dictPrefix}.confirmDelete.subtitle`, {
          name: item.name,
        }),
        btnConfirm: {
          text: t(`${dictPrefix}.confirmDelete.confirm`),
          onConfirm: async () => {
            const { error } = await deleteCostCenter(item.id);
            if (error) {
              addToast({
                icon: 'error',
                color: 'danger',
                text: t(`${dictPrefix}.deleteError`),
              });
              return;
            }
            addToast({
              icon: 'success',
              color: 'success',
              text: t(`${dictPrefix}.deleteSuccess`),
            });
            setRefreshToken((prev) => prev + 1);
          },
        },
        btnClose: { text: t(`${dictPrefix}.confirmDelete.cancel`) },
      });
    },
    [t],
  );

  const columns = useCostCenterColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
  });

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <p className="fs-16 fw-600 text-primary mb-0">
          {t(`${dictPrefix}.title`)}
        </p>
        <Button
          text={t(`${dictPrefix}.add`)}
          size="md"
          outlined
          onClick={handleNew}
        />
      </div>
      {isLoading ? (
        <Loading insideCard />
      ) : (
        <Table
          columns={columns}
          data={items ?? []}
          noResultsText={
            <TableEmptyContent
              title={t(`${dictPrefix}.empty.title`)}
              subtitle={t(`${dictPrefix}.empty.subtitle`)}
            />
          }
        />
      )}
      <CostCenterModal
        isOpen={isModalOpen}
        item={editing}
        onClose={() => setIsModalOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}
